import React, { useState, useEffect } from 'react'
import { Modal, Typography, Avatar, IconButton, ColorCircularProgress } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'constants/index'
import { useDispatch, useSelector } from 'react-redux'
import { getCaseShareProviders } from '../../store/actions/cases'
import { openChatModal } from '../../store/actions/chat/chat'
import { ReactComponent as MessageLogo } from 'static/messages/messages.svg'
import { SvgIcon } from 'components'

const { WHITE } = colors

const useStyles = makeStyles({
  modal: {
    width: 342,
    position: 'relative',
    padding: '12px 16px',
    backgroundColor: WHITE,
    boxShadow: '2px 4px 24px 0px rgba(0, 0, 0, 0.15)',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '24px',
    color: '#2B4157',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  listItem: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    width: '100%',
  },
  avatar: {
    width: 32,
    height: 32,
    flexShrink: 0,
    backgroundColor: '#2B4157',
    color: WHITE,
    fontSize: 13,
    fontWeight: 500,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  nameRow: {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    height: 24,
  },
  name: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '16px',
    color: '#2B4157',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ownerLabel: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '19px',
    color: '#6F8194',
  },
  email: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '18px',
    color: '#6F8194',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatButton: {
    width: 24,
    height: 24,
    padding: 0,
    flexShrink: 0,
    borderRadius: 4,
    '&:hover': {
      backgroundColor: colors.BASIC.COLOR[5],
    },
  },
  chatIcon: {
    width: 16,
    height: 16,
    color: '#2B4157',
  },
})

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : ''
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last || '?'
}

const PeopleWithAccessModal = ({ isOpen, onClose, caseUuid, providers: providedProviders }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const [providers, setProviders] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // If providers are provided as prop, use them directly
    if (providedProviders && Array.isArray(providedProviders)) {
      setProviders(providedProviders)
      setIsLoading(false)
      return
    }

    // Otherwise, fetch from API (backward compatibility)
    if (isOpen && caseUuid) {
      setIsLoading(true)
      dispatch(getCaseShareProviders(caseUuid))
        .then(data => {
          setProviders(Array.isArray(data) ? data : [])
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    } else if (!isOpen) {
      // Reset providers when modal closes
      setProviders([])
    }
  }, [isOpen, caseUuid, dispatch, providedProviders])

  const handleChatClick = (provider, event) => {
    event.stopPropagation()
    onClose()
    if (provider?.twilio_chat_sid) {
      dispatch(openChatModal(provider.twilio_chat_sid, 'activities'))
    }
  }

  const isOwner = provider => {
    return provider?.is_master || provider?.is_owner
  }

  const isCurrentUser = provider => {
    return provider?.username === currentUser?.username
  }

  const handleClose = (event, reason) => {
    if (event) {
      event.stopPropagation()
    }
    onClose(event)
  }

  return (
    <Modal open={isOpen} handleClose={handleClose} maxWidth={false}>
      {isLoading ? (
        <ColorCircularProgress />
      ) : (
        <div className={classes.modal} onClick={e => e.stopPropagation()}>
          <Typography variant="h3" className={classes.title}>
            People with access
          </Typography>
          <div className={classes.list}>
            {providers.map((provider, index) => {
              const fullName = `${provider.first_name || ''} ${provider.last_name || ''}`.trim()
              const initials = getInitials(provider.first_name, provider.last_name)
              const owner = isOwner(provider)

              return (
                <div key={provider.username || index} className={classes.listItem}>
                  <Avatar className={classes.avatar} src={provider.photo_thumbnail || provider.photo || null}>
                    {!provider.photo_thumbnail && !provider.photo && initials}
                  </Avatar>
                  <div className={classes.userInfo}>
                    <div className={classes.nameRow}>
                      <Typography variant="body2" className={classes.name} title={fullName || provider.username}>
                        {fullName || provider.username}
                      </Typography>
                      {owner && (
                        <Typography variant="body2" className={classes.ownerLabel}>
                          (Owner)
                        </Typography>
                      )}
                    </div>
                    <Typography variant="body2" className={classes.email} title={provider.username}>
                      {provider.username}
                    </Typography>
                  </div>
                  {!isCurrentUser(provider) && provider?.twilio_chat_sid && (
                    <IconButton className={classes.chatButton} onClick={e => handleChatClick(provider, e)}>
                      <MessageLogo />
                    </IconButton>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Modal>
  )
}

export default PeopleWithAccessModal
